import { useCallback, useMemo } from 'react'

import type { CartItemId, ConfigurationFlowId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../store'
import { makeConfigKey } from '../store/slices/productConfigSlice'

export interface FlowConfig<T> {
  /** The stored DTO merged over the default, so every top-level field is defined. Stable while the entry is unchanged. */
  value: T
  /** The stored DTO as persisted, possibly partial or absent. */
  raw: Partial<T> | undefined
  /** Merges `partial` over the live entry. Un-confirms the line only when something changed. */
  patch: (partial: Partial<T>) => void
  /** Like `patch`, with the change computed from the live entry. */
  update: (fn: (current: T) => Partial<T>) => void
  isComplete: boolean
}

/**
 * The read/write pair every configuration module keeps its state through: the flow DTO stored at
 * `productConfigs["${cartItemId}|${flowId}"]`. `defaults` must be a module constant.
 *
 * @example
 * const { value, patch } = useFlowConfig(cartItemId, 'measurement', MEASUREMENT_DEFAULT, measurementIsComplete)
 */
export function useFlowConfig<T extends object>(
  cartItemId: CartItemId,
  flowId: ConfigurationFlowId,
  defaults: T,
  isComplete: (dto: T | undefined) => boolean
): FlowConfig<T> {
  const key = makeConfigKey(cartItemId, flowId)
  const raw = usePfStore((s) => s.productConfigs[key] as Partial<T> | undefined)
  const value = useMemo(() => ({ ...defaults, ...raw }), [defaults, raw])

  const update = useCallback((fn: (current: T) => Partial<T>) => {
    const s = usePfStore.getState()
    const current = { ...defaults, ...(s.productConfigs[key] as Partial<T> | undefined) }
    s.patchConfig(cartItemId, flowId, { ...current, ...fn(current) })
  }, [defaults, key, cartItemId, flowId])

  const patch = useCallback((partial: Partial<T>) => update(() => partial), [update])

  return { value, raw, patch, update, isComplete: isComplete(raw as T | undefined) }
}
